import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarRange, Loader2, Mail, Download, Check,
  UserRound, Settings as SettingsIcon, Sparkles,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import api from "../../lib/api";

export default function ExportAnnualForAccountantDialog({ restaurantId }) {
  const [open, setOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth(); // 0-11
  // Default to previous calendar year (typical accountant use-case: cierre enero).
  const [year, setYear] = useState(currentYear - 1);
  const [savedEmail, setSavedEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [busy, setBusy] = useState(null); // 'download' | 'email'

  // Closing-window nudge: Jan–Mar, highlight that the previous fiscal year is
  // ready to send. The typical Spanish accountant deadline is 25-Jan for a
  // "pre-close" pack and 31-Mar for full closing. We pulse the button and
  // show a countdown banner to drive the owner to action without friction.
  const inClosingWindow = currentMonth <= 2; // Jan (0), Feb (1), Mar (2)
  const fiscalYear = currentYear - 1;

  const daysUntilDeadline = (() => {
    if (!inClosingWindow) return null;
    const jan25 = new Date(currentYear, 0, 25, 23, 59, 59);
    const now = new Date();
    const diff = Math.ceil((jan25.getTime() - now.getTime()) / 86400000);
    return diff;
  })();

  useEffect(() => {
    if (!open || !restaurantId) return;
    setEmailLoading(true);
    api.getAccountantEmail(restaurantId)
      .then(r => setSavedEmail(r.accountant_email || ""))
      .catch(() => {})
      .finally(() => setEmailLoading(false));
  }, [open, restaurantId]);

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const onDownload = async () => {
    setBusy("download");
    try {
      const { blob, filename } = await api.downloadFinanceAnnualExport(restaurantId, year);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`ZIP anual ${year} descargado`);
    } catch (e) {
      toast.error(e.message || "Error descargando export anual");
    } finally {
      setBusy(null);
    }
  };

  const onSendEmail = async () => {
    if (!savedEmail) {
      toast.error("Configura primero el email del contable en Ajustes");
      return;
    }
    setBusy("email");
    try {
      const r = await api.sendFinanceAnnualExportEmail(restaurantId, year, savedEmail);
      toast.success(`Año ${r.year} enviado a ${r.sent_to}`);
      setOpen(false);
    } catch (e) {
      toast.error(e.message || "Error enviando email");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div className="relative inline-flex">
          <Button
            size="sm"
            variant={inClosingWindow ? "default" : "outline"}
            className={inClosingWindow ? "bg-primary text-primary-foreground" : ""}
            data-testid="export-annual-accountant-btn"
          >
            <CalendarRange className="h-3.5 w-3.5 mr-1.5" />
            Enviar año completo
          </Button>
          {inClosingWindow && (
            <span
              className="absolute -top-1.5 -right-1.5 flex h-2.5 w-2.5"
              aria-label={`Cierre fiscal ${fiscalYear} disponible`}
              data-testid="annual-closing-pulse"
            >
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
            </span>
          )}
        </div>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="export-annual-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-primary" />
            Enviar año completo al contable
          </DialogTitle>
          <DialogDescription>
            Genera un único ZIP con todas las facturas, gastos, ventas y un P&L mensual
            para los 12 meses del año. Ideal para el cierre fiscal anual.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {inClosingWindow && year === fiscalYear && (
            <div
              className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2"
              data-testid="annual-closing-banner"
            >
              <span className="relative inline-flex h-2.5 w-2.5 mt-1 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
              </span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-900">
                  Cierre fiscal {fiscalYear} disponible
                </p>
                <p className="text-[11px] text-red-800/80 mt-0.5">
                  {daysUntilDeadline > 0
                    ? <>Fecha habitual de entrega al asesor: <b>25 de enero</b> · {daysUntilDeadline} {daysUntilDeadline === 1 ? "día" : "días"} restantes.</>
                    : <>La fecha habitual de entrega al asesor (25 de enero) ya ha pasado. Envía cuanto antes para no retrasar el cierre.</>}
                </p>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="export-year" className="text-xs font-medium">
              Año a exportar
            </label>
            <select
              id="export-year"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="export-year-select"
            >
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* Email read-only */}
          <div
            className="rounded-lg border border-border bg-muted/40 p-3 flex items-center gap-3"
            data-testid="annual-email-readonly"
          >
            <UserRound className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-muted-foreground uppercase tracking-wide font-semibold">
                Contable
              </div>
              {emailLoading ? (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Cargando…</span>
                </div>
              ) : savedEmail ? (
                <div className="text-sm font-medium truncate flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 text-emerald-700" />
                  <span className="truncate">{savedEmail}</span>
                  <Check className="h-3 w-3 text-emerald-700 shrink-0" />
                </div>
              ) : (
                <div className="text-xs text-amber-700">Sin email configurado</div>
              )}
            </div>
            <Button
              asChild
              size="sm"
              variant="ghost"
              className="shrink-0"
              data-testid="annual-email-edit-link"
            >
              <Link to="/app/settings?tab=profile">
                <SettingsIcon className="h-3.5 w-3.5 mr-1" />
                {savedEmail ? "Editar" : "Configurar"}
              </Link>
            </Button>
          </div>

          <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3 text-xs text-indigo-900 space-y-1">
            <p className="font-semibold flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> El ZIP anual incluye:
            </p>
            <ul className="list-disc list-inside space-y-0.5 text-indigo-800/90">
              <li><code className="text-[10px] bg-card px-1 rounded">facturas/YYYY-MM/</code> todas las facturas agrupadas por mes</li>
              <li><code className="text-[10px] bg-card px-1 rounded">gastos.csv</code> del año completo</li>
              <li><code className="text-[10px] bg-card px-1 rounded">ventas.csv</code> desglose diario por canal de los 12 meses</li>
              <li><code className="text-[10px] bg-card px-1 rounded">resumen_anual.csv</code> 12 filas + TOTAL del año</li>
              <li><code className="text-[10px] bg-card px-1 rounded">resumenes_mensuales/*.pdf</code> un P&L por mes con actividad</li>
            </ul>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={onDownload}
            disabled={!!busy}
            data-testid="export-annual-download-btn"
          >
            {busy === "download"
              ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              : <Download className="h-3.5 w-3.5 mr-1.5" />}
            Descargar ZIP
          </Button>
          <Button
            onClick={onSendEmail}
            disabled={!!busy || !savedEmail}
            data-testid="export-annual-send-email-btn"
          >
            {busy === "email"
              ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              : <Mail className="h-3.5 w-3.5 mr-1.5" />}
            Enviar al contable
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
